import { NavLink, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Link2,
  QrCode,
  Plus,
  Sparkles,
  Megaphone,
  LayoutTemplate,
  Palette,
  HelpCircle,
} from "lucide-react";
import Button from "../ui/Button";
import { useMarketplace } from "../../marketplace/context";
import env from "../../config";

const navItems = [
  { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "links", label: "Links", icon: Link2 },
  { to: "campaigns", label: "Campaigns", icon: Megaphone },
  { to: "landing-pages", label: "Pages", icon: LayoutTemplate },
  // Analytics list hidden — analytics is opened per-link from the Links page.
  // { to: "analytics", label: "Analytics", icon: BarChart3 },
  { to: "qr", label: "QR Codes", icon: QrCode },
  { to: "white-label", label: "White-label", icon: Palette },
];

const tierStyles: Record<string, string> = {
  starter:
    "bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 ring-amber-200/60",
  pro: "bg-gradient-to-r from-brand-100 to-violet-100 text-brand-800 ring-brand-200/60",
};

const BrandMark = ({ buildPath }: { buildPath: (s: string) => string }) => (
  <RouterLink
    to={buildPath("dashboard")}
    className="flex items-center gap-2.5 font-display font-bold text-ink-900 tracking-tight"
  >
    <span className="relative">
      <span className="absolute -inset-1 bg-brand-gradient opacity-30 blur-md rounded-xl" />
      <span className="relative w-8 h-8 rounded-xl bg-brand-gradient flex items-center justify-center shadow-brand-glow">
        <Link2 size={15} className="text-white" strokeWidth={2.75} />
      </span>
    </span>
    <span className="text-[15px] tracking-tight">
      Smart{" "}
      <span className="bg-brand-gradient bg-clip-text text-transparent">
        Links
      </span>
    </span>
  </RouterLink>
);

const DefaultHeader = () => {
  const navigate = useNavigate();
  const { buildPath, tier, tierLabel, openUpgrade } = useMarketplace();

  return (
    <header className="sticky top-0 z-40 w-full bg-white/75 backdrop-blur-xl border-b border-ink-200/60">
      <div className="mx-auto max-w-7xl px-6 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-7">
          <BrandMark buildPath={buildPath} />
          <span className="hidden md:block w-px h-5 bg-ink-200" />
          <nav className="hidden md:flex items-center gap-0.5">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={buildPath(to)}
                className={({ isActive }) =>
                  `group relative px-3 h-8 inline-flex items-center gap-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? "text-ink-900"
                      : "text-ink-500 hover:text-ink-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute inset-0 bg-ink-100/80 rounded-lg" />
                    )}
                    <Icon
                      size={14}
                      strokeWidth={2.25}
                      className="relative z-10"
                    />
                    <span className="relative z-10">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* Help — opens the standalone user-guide site in a new tab. */}
          {env.DOCS_URL && (
            <a
              href={env.DOCS_URL}
              target="_blank"
              rel="noreferrer"
              title="Help & user guide"
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-ink-500 hover:text-ink-900 hover:bg-ink-100 ring-1 ring-ink-200/70 transition-colors"
            >
              <HelpCircle size={15} strokeWidth={2.25} />
            </a>
          )}
          {/* Single plan pill — shows the current plan; click to compare plans
              and see how to switch. */}
          <button
            onClick={openUpgrade}
            title={
              tier === "pro"
                ? "Your plan: Pro — click to view plans"
                : `Current plan: ${tierLabel} — click to change plan`
            }
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold ring-1 hover:saturate-150 transition-all ${tierStyles[tier]}`}
          >
            <Sparkles size={12} />
            <span>{tierLabel}</span>
          </button>
          <Button
            onClick={() => navigate(buildPath("links?new=1"))}
            variant="primary"
            size="sm"
            icon={<Plus size={14} strokeWidth={2.5} />}
          >
            <span className="hidden sm:inline">Create link</span>
          </Button>
        </div>
      </div>

      <nav className="md:hidden flex items-center gap-1 px-4 pb-2.5 overflow-x-auto">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={buildPath(to)}
            className={({ isActive }) =>
              `shrink-0 px-3 h-8 inline-flex items-center gap-1.5 rounded-lg text-xs font-medium ${
                isActive
                  ? "text-ink-900 bg-ink-100"
                  : "text-ink-500 hover:bg-ink-100"
              }`
            }
          >
            <Icon size={13} />
            {label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

export default DefaultHeader;
